import { useState } from 'react';
import { PRESS_STORIES, type PressOutlet } from '../content/press';
import { trackLandingEvent } from '../lib/analytics';

function pagePath(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '/';
}

/** Renders the masthead, falling back to the outlet name if the logo is absent or fails. */
function PressMark({ outlet }: { outlet: PressOutlet }) {
  const [failed, setFailed] = useState(false);

  if (!outlet.logo || failed) {
    return <span className="press-wordmark">{outlet.outlet}</span>;
  }

  return (
    <img
      src={outlet.logo}
      alt={outlet.outlet}
      width={outlet.width}
      height={outlet.height}
      sizes={`${outlet.logoClass?.match(/max-w-\[(\d+)px\]/)?.[1] ?? 150}px`}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className={`press-logo w-auto object-contain ${outlet.logoClass ?? 'h-[28px] max-w-[150px]'}`}
    />
  );
}

export function PressStrip() {
  return (
    <section className="band-dark" id="press-strip" aria-labelledby="press-strip-label">
      <div className="container">
        <p id="press-strip-label" className="label-mono m-0 reveal">- Featured in</p>

        <div className="mt-11 grid gap-x-14 gap-y-12 lg:grid-cols-2">
          {PRESS_STORIES.map((story, i) => (
            <article
              key={story.id}
              className={`reveal ${i === 0 ? 'd1' : 'd2'}${i > 0 ? ' lg:border-l lg:border-outline-variant lg:pl-14' : ''}`}
            >
              <h3 className="press-headline m-0">{story.headline}</h3>

              <ul className="press-outlets m-0 mt-7 flex list-none flex-wrap items-center gap-x-10 gap-y-6 pl-0">
                {story.outlets.map((outlet) => (
                  <li key={outlet.id}>
                    <a
                      href={outlet.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${outlet.outlet}: ${story.headline} (opens in a new tab)`}
                      onClick={() => {
                        trackLandingEvent('press_link_click', {
                          location: 'press_strip',
                          publication: outlet.outlet,
                          page_path: pagePath(),
                        });
                      }}
                      className="press-link inline-flex items-center"
                    >
                      <PressMark outlet={outlet} />
                    </a>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
